import React from 'react';
import { notifySearchFailure } from '../../utils/searchFailureToast';
import { inject, observer } from 'mobx-react';
import { toJS } from 'mobx';
import rootStore, { IMobxRootState } from '../../RootStore';
import moment from 'moment';
import {
    E_RISK_TYPE,
    IActiveCountry,
    IActiveDimension,
    IActiveGroup,
    IAPIActiveClientFacingIndicator,
    RiskMarkerData,
    VIEW,
    DATE_RANGE_VIEWS,
} from '../../interfaces';
import {
    EVENTS_FILTER,
    IEventsData,
    IUscoreActive,
    SCORED_EVENTS_FILTER,
} from './EventsFeedInterfaces';
import * as _ from 'lodash';
import * as IPC from './EventsWorkerIPC';
import EventsFeedStore from './EventsFeedStore';
import { generateID } from '../../utils/generalUtils';
import { C_TIMELINE_MIN_DATE, MOMENTJS_DATE_DISPLAY_FORMAT } from '../../constants';
import {
    flow,
    map,
    get,
    flatten,
    uniq,
    keyBy,
    find,
    includes,
    toArray,
    isEmpty,
} from 'lodash/fp';
import { RouteComponentProps, withRouter } from 'components/withRouter';
import { PickerDrawer } from 'components/Pickers/PickerDrawer';
import EventsFeed from './EventsFeed';
import RiskMarkerEvents from './RiskMarkerEvents';
import { buildMockClusterEvent, isShowMockClusterEvent } from './clusterMock';

type Props = RouteComponentProps & {
    heatmapCountries?: number[];
    heatmapRisks?: number[];
    countries?: number[];
    eventFeedCountries?: number[];
    risks?: number[];
    virtualToday?: moment.Moment;
    feedFilter?: EVENTS_FILTER;
    scoredEventsFilter?: SCORED_EVENTS_FILTER;
    selectedUscore?: IUscoreActive;
    hoveredUscore?: IUscoreActive;
    countryPageId?: number;
    activeGroupId?: string;
    activeGroups?: IActiveGroup[];
    searchString?: string;
    includeMentionCountries?: boolean;
    dimensions?: { [dimensionId: number]: IActiveDimension };
    activeClientFacingIndicators?: IAPIActiveClientFacingIndicator;
    onEventsClick?:  (id: number) => void;
    riskType?: E_RISK_TYPE;
    activeEventID?: number;
    activeFilter?: EVENTS_FILTER;
    isCollapsed?: boolean;
    selectedCountries?: number[];
    onViewChange?: (view: VIEW, ...args: any[]) => void;
    onTopIndicatorClicked?: (dimensionName: string, countryId: number) => void;
    canDeleteEvents?: boolean;
    showSearchBar?: boolean;
    searchFocused?: boolean;
    licensedCountriesArr?: IActiveCountry[];
    impactFilter?: number[];
    maxDate?: moment.Moment;
    minDate?: moment.Moment;
    chartMinDate?: moment.Moment;
    riskMarkerEvent?: RiskMarkerData;
};

type State = {
    events?: IEventsData[];
    tempEvents?: IEventsData[];
    loadInProgress: boolean;
    loadFailed: boolean;
};

const getEventsWorker = () => {
    return rootStore.eventsFeedStore.eventsHandlerWorker;
};

const MIN_FEED_EVENTS = 30;
const MAX_EVENTS_DAYS_TO_FETCH = 2 ** 5; // 32 days, rougly 1 month, also consistent with the exponential backoff I'm using with this

@inject(
    ({
        countryStore,
        risksStore,
        eventsFeedStore,
        chartStore,
        scoreEventStore,
        settingsStore,
        UserStore,
        heatmapStore,
    }: IMobxRootState): Partial<Props> => {
        const baseDate = chartStore?.baseDate;

        return {
            heatmapCountries: [heatmapStore.selectedCountry].filter(Boolean),
            countries: countryStore.currentCountryList,
            eventFeedCountries: eventsFeedStore.selectedCountries,
            heatmapRisks: [heatmapStore.selectedRisk].filter(Boolean),
            risks: risksStore.currentList,
            virtualToday: chartStore.virtualToday,
            feedFilter: eventsFeedStore.currentFilter,
            scoredEventsFilter: eventsFeedStore.scoredEventsFilter,
            selectedUscore: eventsFeedStore.currentSelectedUscore,
            hoveredUscore: eventsFeedStore.currentHoveredUscore,
            countryPageId: null,
            activeGroupId: settingsStore.activeGroupId,
            activeGroups: settingsStore.activeGroups,
            searchString: eventsFeedStore.searchString,
            includeMentionCountries:
                UserStore.geoquantFlagsPermission.includeMentionCountries,
            dimensions: keyBy(r => r.id, risksStore.dimensions.flatten),
            activeClientFacingIndicators: risksStore.clientFacingIndicators,
            riskType: risksStore.currentRisksType,
            activeEventID:
                scoreEventStore.activeEvent !== null
                    ? scoreEventStore.activeEvent.id
                    : null,
            activeFilter: eventsFeedStore.currentFilter,
            isCollapsed: eventsFeedStore.isCollapsed,
            canDeleteEvents: UserStore.uscoresPermissions.canDelete,
            showSearchBar: eventsFeedStore.showSearchBar,
            searchFocused: eventsFeedStore.searchFocused,
            licensedCountriesArr: countryStore.countries.licensedCountriesArr,
            impactFilter: eventsFeedStore.impactFilter,
            maxDate: eventsFeedStore.maxDate,
            minDate: eventsFeedStore.minDate,
            chartMinDate: baseDate
                ? baseDate.clone().startOf('day').subtract(chartStore.rangeLeft, 'days')
                : null,
            riskMarkerEvent: chartStore.riskMarkerEvent,
        };
    }
)
@observer
class EventsFeedDataProvider extends React.Component<
    Props,
    State
> {
    private static deletedEvents = new Set<number>();
    private mounted: boolean = false;
    private currentTimeout: any = null;
    private numOfEventsDaysToFetch: number;
    private numEventsReceivedInCurrentBatch: number;
    private totalDaysFetched: number;
    private latestRequestId: string = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            events: [],
            tempEvents: [],
            loadInProgress: true,
            loadFailed: false,
        };
        this.resetFetchEventsExponentialBackoffParams();
    }

    public componentDidMount() {
        this.mounted = true;
        const worker = getEventsWorker();
        if (worker) {
            worker.onmessage = this.onWorkerMessage;
        }
        this.reloadEventsFeed(false);
        this.handleActiveUscores();
    }

    private isHeatmap(): boolean {
        return this.props.location.pathname.includes(VIEW.HEATMAP);
    }
    private isEventPage(): boolean {
        return this.props.location.pathname.includes(VIEW.RISK_INTELLIGENCE);
    }
    private isEventScoringPage(): boolean {
        return this.props.location.pathname.includes(VIEW.SCORE_EVENT);
    }

    private get selectedRisksIds(): number[] {
        return this.isHeatmap() ? this.props.heatmapRisks : this.props.risks;
    }

    private get selectedCountriesIds(): number[] {
        if (this.isHeatmap()) {
            return this.props.heatmapCountries;
        }
        if (this.isEventPage() || this.isEventScoringPage()) {
            return this.props.eventFeedCountries;
        }
        return this.props.countries;
    }

    private getRisks(): number[] {
        if (
            includes(this.props.riskType, [
                E_RISK_TYPE.DIMENSIONS,
                E_RISK_TYPE.CONTINGENT,
            ])
        ) {
            return this.selectedRisksIds;
        }

        return flow(
            map((risk: number) => {
                return flow(
                    get([risk, 'composing_dimensions']),
                    map(get('id'))
                )(this.props.activeClientFacingIndicators);
            }),
            flatten,
            uniq
        )(this.selectedRisksIds);
    }

    public componentWillUnmount() {
        this.mounted = false;
        const worker = getEventsWorker();
        if (worker) {
            worker.onmessage = null;
        }
    }
    public componentDidUpdate(prevProps: Props, prevState: State) {
        if (
            !_.isEqual(prevProps.hoveredUscore, this.props.hoveredUscore) ||
            !_.isEqual(prevProps.selectedUscore, this.props.selectedUscore)
        ) {
            this.handleActiveUscores();
        } else if (
            this.props.activeGroupId !== prevProps.activeGroupId &&
            prevProps.activeGroupId &&
            prevProps.activeGroupId !== null
        ) {
            this.reloadEventsFeed(true);
            return;
        } else if (
            prevProps.feedFilter !== this.props.feedFilter &&
            prevProps.feedFilter
        ) {
            this.reloadEventsFeed(true);
            return;
        } else if (
            prevProps.scoredEventsFilter !== this.props.scoredEventsFilter
        ) {
            this.reloadEventsFeed(true);
            return;
        } else if (
            (prevProps.virtualToday &&
            !prevProps.virtualToday.isSame(this.props.virtualToday, 'days')) ||
            (prevProps.maxDate &&
            !prevProps.maxDate.isSame(this.props.maxDate, 'days')) ||
            (prevProps.minDate &&
            !prevProps.minDate.isSame(this.props.minDate, 'days')) ||
            (!prevProps.maxDate && this.props.maxDate) ||
            (!prevProps.minDate && this.props.minDate) ||
            (prevProps.chartMinDate &&
            !prevProps.chartMinDate.isSame(this.props.chartMinDate, 'days')) ||
            (!prevProps.chartMinDate && this.props.chartMinDate))
         {
             this.reloadEventsFeed(true);
             return;
         }
        else if (!_.isEqual(prevProps.impactFilter, this.props.impactFilter)){
            this.reloadEventsFeed(true);
            return;
        } else if (prevProps.searchString !== this.props.searchString) {
            if (isEmpty(this.props.searchString)) {
                this.reloadEventsFeed(true);
            } else {
                this.reloadEventsFeedSearch(this.props.searchString);
            }
            return;
        } else if (
            !_.isEqual(
                prevProps.heatmapCountries,
                this.props.heatmapCountries
            ) ||
            !_.isEqual(prevProps.eventFeedCountries, this.props.eventFeedCountries) ||
            !_.isEqual(prevProps.heatmapRisks, this.props.heatmapRisks) ||
            !_.isEqual(prevProps.countries, this.props.countries) ||
            !_.isEqual(prevProps.risks, this.props.risks) ||
            !_.isEqual(prevProps.feedFilter, this.props.feedFilter) ||
            !_.isEqual(prevProps.activeGroupId, this.props.activeGroupId)
        ) {
            this.reloadEventsFeed(true);
        }

        if (this.state.events === prevState.events) return;
        if (!_.isEmpty(this.state.events)) {
            const newDefaultEvent = _.find(
                this.state.events,
                event => !EventsFeedDataProvider.deletedEvents.has(event.id)
            );
            EventsFeedStore.setDefaultEvent(newDefaultEvent.id);
            return;
        }
        if (!this.state.loadInProgress) {
            EventsFeedStore.setDefaultEvent(null);
        }
    }

    public render() {
        if (this.props.riskMarkerEvent) {
            return <RiskMarkerEvents />;
        }
        const filteredEvents = this.filterByMinDate(this.state.events).filter(
            event => !EventsFeedDataProvider.deletedEvents.has(event.id)
        );
        const eventsToRender =
            isShowMockClusterEvent(this.props.location.search) &&
            this.isEventScoringPage()
                ? [buildMockClusterEvent(filteredEvents), ...filteredEvents]
                : filteredEvents;
        // allows react-window to efficiently render items without comparing moemntjs dates on the fly
        const eventsWithDateString = _.map(eventsToRender, event => ({
            ...event,
            dateString: moment(event.time).format(MOMENTJS_DATE_DISPLAY_FORMAT),
        }));

        return (
            <PickerDrawer title="All Events" side="left" defaultOpen>
                <EventsFeed
                    onEventClick={
                        this.props.onEventsClick || this.onEventsClick
                    }
                    events={eventsWithDateString}
                    isLoading={this.state.loadInProgress}
                    loadFailed={this.state.loadFailed}
                    loadMoreEvents={this.loadMoreEvents}
                    onEventDelete={this.onEventDelete}
                    activeEventID={this.props.activeEventID}
                    canDeleteEvents={this.props.canDeleteEvents}
                    dimensions={this.props.dimensions}
                    includeMentionCountries={this.props.includeMentionCountries}
                    licensedCountriesArr={this.props.licensedCountriesArr}
                    impactFilter={this.props.impactFilter}
                    maxDate={this.props.maxDate}
                    minDate={this.props.minDate}
                    selectedCountries={this.props.eventFeedCountries}
                    searchString={this.props.searchString}
                    feedFilter={this.props.feedFilter}
                    scoredEventsFilter={this.props.scoredEventsFilter}
                    isShowCountryFilter={this.isEventPage() || this.isEventScoringPage()}
                    isShowDateFilter={this.isEventPage() || this.isEventScoringPage()}
                />
            </PickerDrawer>
        );
    }

    private onEventDelete = (id: number) => {
        EventsFeedDataProvider.deletedEvents.add(id);
        if (this.mounted) {
            this.forceUpdate();
        }
    };

    private loadMoreEvents = () => {
        if (
            this.state.loadInProgress ||
            this.state.loadFailed ||
            isEmpty(this.state.events) ||
            this.hasReachedStartDate(this.state.events)
        ) {
            return;
        }
        this.setAndFetchEvents();
    };

    private onWorkerMessage = (msg: MessageEvent) => {
        const response = msg.data as IPC.IEventsWorkerIPCResponse;
        if (!this.mounted || response.requestId !== this.latestRequestId) {
            return;
        }
        if (response.code === IPC.E_EVENTS_WORKER_IPC_RESPONSE_CODES.ERROR) {
            this.handleFetchError();
            return;
        }

        const receivedEvents = [];
        response.data.forEach(e => {
            if (!EventsFeedDataProvider.deletedEvents.has(e.id)) {
                e.time = moment(e.time);
                e.created_at = moment(e.created_at);
                receivedEvents.push(e);
            }
        });
        switch (response.code) {
            case IPC.E_EVENTS_WORKER_IPC_RESPONSE_CODES.EVENTS:
                if (!this.mounted) {
                    return;
                }

                const events = this.state.events.concat(receivedEvents);
                const defaultEvent = events[0];
                if (
                    defaultEvent &&
                    EventsFeedStore.defaultEvent !== defaultEvent.id
                ) {
                    EventsFeedStore.setDefaultEvent(defaultEvent.id);
                }
                this.numEventsReceivedInCurrentBatch +=
                    receivedEvents.length;
                this.totalDaysFetched += this.numOfEventsDaysToFetch;

                const doubledDays = this.numOfEventsDaysToFetch * 2;
                const hasDateRange = !!this.getMinDate();
                const nextNumOfEventsDaysToFetch = hasDateRange
                    ? Math.min(doubledDays, MAX_EVENTS_DAYS_TO_FETCH)
                    : doubledDays;
                const shouldContinueBackfill =
                    this.numEventsReceivedInCurrentBatch <
                        MIN_FEED_EVENTS &&
                    (hasDateRange || doubledDays <= MAX_EVENTS_DAYS_TO_FETCH) &&
                    !this.hasReachedStartDate(events) &&
                    !(hasDateRange && this.hasCoveredEntireRange());

                if (shouldContinueBackfill) {
                    this.numOfEventsDaysToFetch = nextNumOfEventsDaysToFetch;
                    this.setAndFetchEvents(events);
                } else {
                    this.resetBatchBackoffParams();
                    this.finishEventsLoad(events);
                }
                break;
            case IPC.E_EVENTS_WORKER_IPC_RESPONSE_CODES.USCORE_EVENTS:
                const activeUscore =
                    this.props.hoveredUscore || this.props.selectedUscore;
                if (
                    activeUscore &&
                    activeUscore.asId === response.uscore.asId &&
                    this.mounted
                ) {
                    this.setState({
                        tempEvents: receivedEvents,
                    });
                }
                break;
        }
    };

    private handleFetchError() {
        this.setState({ loadInProgress: false });
        if (this.props.searchString) {
            notifySearchFailure('events', () => EventsFeedStore.setSearchString(''));
        } else {
            this.setState({ loadFailed: true });
        }
    }

    private resetBatchBackoffParams() {
        this.numOfEventsDaysToFetch = 1;
        this.numEventsReceivedInCurrentBatch = 0;
    }

    private resetFetchEventsExponentialBackoffParams() {
        this.resetBatchBackoffParams();
        this.totalDaysFetched = 0;
    }

    private filterByMinDate(events: IEventsData[]): IEventsData[] {
        const minDate = this.getMinDate();
        if (!minDate) return events;
        return events.filter(e => moment(e.time).isSameOrAfter(minDate, 'day'));
    }

    private hasReachedStartDate(events: IEventsData[]): boolean {
        const minDate = this.getMinDate();
        if (!minDate || isEmpty(events)) return false;
        const oldest = moment.min(events.map(e => moment(e.time)));
        return oldest.isSameOrBefore(minDate, 'day');
    }

    private hasCoveredEntireRange(): boolean {
        const minDate = this.getMinDate();
        if (!minDate) return false;
        return this.getRangeBackfillDate().isBefore(minDate, 'day');
    }

    private getMaxDate(){
        if (this.isEventPage() || this.isEventScoringPage()) {
            return this.props.maxDate || moment().startOf('day');
        }
        return this.props.virtualToday;
    }

    private isSearchMode(): boolean {
        return !isEmpty(this.props.searchString);
    }

    // chartStore range is shared across views, so only treat chartMinDate as a
    // lower bound on views that actually expose the chart date-range picker.
    // Otherwise it may just be stale state from another tab (for example Heatmap).
    private hasChartDateRangePicker(): boolean {
        return DATE_RANGE_VIEWS.some(view =>
            this.props.location.pathname.includes(view)
        );
    }

    private getMinDate(): moment.Moment {
        if (this.isEventPage() || this.isEventScoringPage()) {
            return this.props.minDate;
        }
        if (this.hasChartDateRangePicker()) {
            return C_TIMELINE_MIN_DATE;
        }
        return null;
    }

    private clearFeedForNewFilter() {
        this.latestRequestId = null;
        this.setState({ events: [], loadInProgress: true, loadFailed: false });
    }

    private reloadEventsFeed(wait: boolean = false) {
        rootStore.eventsFeedStore.currentEventIds = [];
        if (!this.currentTimeout) this.clearFeedForNewFilter();
        const latestDate = this.getMaxDate().clone()
        clearTimeout(this.currentTimeout);
        const fn = () => {
            this.currentTimeout = null;
            if (!this.mounted) {
                return;
            }
            if (this.getMaxDate().isSame(latestDate, 'day')) {
                this.resetFetchEventsExponentialBackoffParams();
                this.setAndFetchEvents([]);
            }
        };
        this.currentTimeout = setTimeout(fn, wait ? 300 : 0);
    }

    private reloadEventsFeedSearch(searchString: string) {
        rootStore.eventsFeedStore.currentEventIds = [];
        const timeout = searchString.length <= 3 ? 200 : 100;
        setTimeout(() => {
            if (!this.mounted) {
                return;
            }
            if (this.props.searchString === searchString) {
                this.setAndFetchEvents([]);
            }
        }, timeout);
    }

    private finishEventsLoad(events: IEventsData[]) {
        this.setState({
            events,
            loadInProgress: false,
        });
        rootStore.eventsFeedStore.currentEventIds = this.filterByMinDate(events).map(e => String(e.id));
    }

    private setAndFetchEvents = (eventsToSet: IEventsData[] = null) => {
        if (this.mounted) {
            const newState: any = {
                loadInProgress: true,
                loadFailed: false,
            };
            if (eventsToSet) {
                newState.events = eventsToSet;
            }
            this.setState(newState, () => this.fetchEvents());
        }
    };

    private fetchEvents() {
        this.sendEventWorkerRequest({
            code: IPC.E_EVENTS_WORKER_IPC_CODES.FETCH_EVENTS,
            props: this.getIPCEventsRequestProps(),
        });
    }

    private getRangeBackfillDate(): moment.Moment {
        return this.getMaxDate().clone().subtract(this.totalDaysFetched, 'days');
    }

    private getNextFetchMaxDate(): moment.Moment {
        if (this.state.events.length > 0) {
            const minEventDate = moment.min(this.state.events.map(e => e.time));

            if (this.isSearchMode()) {
                return minEventDate.clone().subtract(1, 'days');
            }

            if (this.getMinDate() && this.totalDaysFetched > 0) {
                return this.getRangeBackfillDate();
            }

            return minEventDate.clone().subtract(1, 'days');
        }

        if (this.getMinDate() && this.totalDaysFetched > 0) {
            return this.getRangeBackfillDate();
        }

        return this.getMaxDate().isAfter(moment(), 'days')
            ? moment().startOf('day')
            : this.getMaxDate().clone();
    }

    private getIPCEventsRequestProps(): IPC.IEventWorkerIPCExecuteActionProps {
        const maxDate = this.getNextFetchMaxDate();
        const activeGroupCountries = !this.props.activeGroupId
            ? []
            : flow(
                  find(
                      (group: IActiveGroup) =>
                          group.id === this.props.activeGroupId
                  ),
                  get('countries'),
                  toArray
              )(this.props.activeGroups);

        return {
            maxDate: maxDate.toISOString(),
            feedType: this.props.feedFilter,
            numDaysToFetch: this.numOfEventsDaysToFetch,
            activeGroupCountries,
            selectedRisks: this.getRisks(),
            limit: MIN_FEED_EVENTS,
            searchString: this.props.searchString,
            scoredEventsFilter: this.props.scoredEventsFilter,
            includeMentionedCountries: this.props.includeMentionCountries,
            riskType: this.props.riskType,
            //todo: remove spread after fixing impaxt type in worker
            impactFilter: [...this.props.impactFilter],
            selectedCountries: [...this.selectedCountriesIds],
        };
    }

    private async handleActiveUscores() {
        const isSelectedUscoreAndCountry =
            this.props.selectedUscore &&
            _.intersection(
                this.selectedCountriesIds,
                this.props.selectedUscore.countryIds
            ).length > 0;
        const activeUscore =
            this.props.hoveredUscore || this.props.selectedUscore;
        if (
            !activeUscore ||
            (this.props.selectedUscore && !isSelectedUscoreAndCountry)
        ) {
            if (this.state.tempEvents.length !== 0) {
                this.setState({
                    tempEvents: [],
                });
            }
            return;
        }
        this.sendEventWorkerRequest({
            code: IPC.E_EVENTS_WORKER_IPC_CODES.FETCH_TEMP_EVENTS,
            uscore: toJS(activeUscore),
        });
    }

    private onEventsClick = (id: number) => {
        if (!this.props.location.pathname.includes(id.toString())) {
            this.props.navigate(`/${VIEW.RISK_INTELLIGENCE}/events/${id}`);
        }
    };

    private sendEventWorkerRequest = (
        msg: Partial<
            | IPC.IEventWorkerIPCExecuteAction
            | IPC.IEventsWorkerIPCHandleActiveUscore
        >
    ) => {
        this.latestRequestId = generateID();
        msg.requestId = this.latestRequestId;
        getEventsWorker().postMessage(msg);
    };
}

export default withRouter(EventsFeedDataProvider);